import type {
  LogicalAlignment,
  WithInherit,
} from '@wix/editor-elements-common-utils';
import {
  castFromInheritIfNeeded,
  convertPhysicalInputAlignmentToDirection,
  convertPhysicalInputAlignmentToLogical,
  getScaledFont,
  withFallbackValues,
  withSiteDirection,
} from '@wix/editor-elements-common-utils';
import {
  withCompInfo,
  createComponentMapperModel,
  withStateRefsValues,
} from '@wix/editor-elements-integrations';
import type { CheckboxGroupProps as CheckboxGroupPlatformProps } from '@wix/thunderbolt-components';
import { generateCheckboxCSSVars } from '../../Checkbox/viewer/Checkbox.mapper';

import type {
  ICheckboxGroupStateValues,
  CheckboxGroupCSSVars,
  CheckboxGroupDefinition,
} from '../CheckboxGroup.types';
import { translationKeys } from './constants';

export const props = withCompInfo<
  CheckboxGroupPlatformProps,
  CheckboxGroupDefinition,
  CheckboxGroupPlatformProps
>()(
  [
    'fullNameCompType',
    'language',
    'compId',
    'compProps',
    'trackClicksAnalytics',
    'translate',
  ],
  (compInfo, carmiData) => {
    const {
      compProps: { errorMessageType },
      translate,
      ...restCompInfo
    } = compInfo;

    const fallbackValidationMessage = 'Select at least one option.';

    return {
      ...carmiData,
      ...restCompInfo,
      errorMessageType,
      translations: {
        errorMessage: translate(
          translationKeys.NAMESPACE,
          translationKeys.INLINE_ERROR_MESSAGE_DEFAULT_ERROR_MESSAGE,
        ),
        requiredAriaLabel: translate(
          translationKeys.NAMESPACE,
          translationKeys.CHECKBOX_GROUP_REQUIRED_ARIA_LABEL,
        ),
        validationMessage:
          translate(
            translationKeys.NAMESPACE,
            translationKeys.INLINE_ERROR_MESSAGE_DEFAULT_VALIDATION_MESSAGE,
          ) || fallbackValidationMessage,
      },
    };
  },
);

export const css = withCompInfo<
  CheckboxGroupCSSVars,
  CheckboxGroupDefinition,
  never,
  {
    styleProperties: {
      align: LogicalAlignment;
      optionsAlign: WithInherit<LogicalAlignment>;
      txtlblrq?: string;
    };
  }
>()(
  [
    'compId',
    'compLayout',
    'compProps',
    'styleProperties',
    'isMobileView',
    'hasResponsiveLayout',
    'compData',
    'siteFonts',
    'formatCssValue',
    'experiments',
    'structure',
    'siteFontsSpxResolved',
  ],
  compInfo => {
    const {
      compProps,
      styleProperties: { align, optionsAlign, txtlblrq },
      compData,
    } = compInfo;

    const {
      direction = 'inherit',
      labelDirection = 'inherit',
      optionsDirection = 'inherit',
      label,
    } = compData;

    const {
      labelMargin,
      layout,
      buttonSize,
      buttonsMargin,
      labelMobileFontSize,
      spacing,
    } = compProps;

    const calculatedLabelMargin =
      label?.length > 0 && labelMargin ? labelMargin : 0;

    const notTransparentText = txtlblrq && txtlblrq !== 'transparent';

    const fnt2 = getScaledFont(compInfo, 'fnt2', labelMobileFontSize);
    const groupLabelMargin = `0 0 ${calculatedLabelMargin}px 0`;

    const checkboxCssVars = generateCheckboxCSSVars(compInfo, {
      size: buttonSize,
      labelMarginStart: `${spacing + 1}px`,
    });

    const calculatedOptionsAlignment = castFromInheritIfNeeded(
      optionsAlign,
      align,
    );

    const varsByLayout = {
      horizontal: {
        '--itemsFlexDirection': 'row',
        '--itemsJustifyContent': calculatedOptionsAlignment,
        '--itemsAlignItems': 'unset',
      },
      vertical: {
        '--itemsFlexDirection': 'column',
        '--itemsJustifyContent': 'unset',
        '--itemsAlignItems': calculatedOptionsAlignment,
      },
    } as const;

    return {
      ...checkboxCssVars,
      ...varsByLayout[layout],
      '--fnt2': fnt2,
      '--rootDirection': withSiteDirection(direction),
      '--labelDirection': labelDirection,
      '--checkbox-direction': optionsDirection,
      '--groupLabelMargin': groupLabelMargin,
      '--align': align,
      '--requiredIndicationDisplay': notTransparentText ? 'inline' : 'none',
      '--itemsMargin': `${buttonsMargin}px`,
    };
  },
  [
    withFallbackValues([
      {
        sourceNamespace: 'compProps',
        targetNamespace: 'styleProperties',
        fields: [{ source: 'alignment', target: 'align' }],
        enhancer: convertPhysicalInputAlignmentToLogical,
      },
      {
        sourceNamespace: 'compProps',
        targetNamespace: 'compData',
        fields: [{ source: 'alignment', target: 'direction' }],
        enhancer: convertPhysicalInputAlignmentToDirection,
      },
    ]),
  ],
);

const stateRefs = withStateRefsValues<keyof ICheckboxGroupStateValues>([
  'reportBi',
]);

export default createComponentMapperModel({ props, css, stateRefs });
